import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  projectsAtom,
  fetchProjectsAtom,
  updateProjectAtom,
  projectByIdAtom,
  deleteProjectAtom
} from '../../atoms/v0/projectAtoms';
import {
  tasksAtom,
  fetchTasksAtom,
  updateTaskAtom
} from '../../atoms/v0/taskAtoms';
import {
  sidebarCollapsedAtom,
  chatSidebarCollapsedAtom,
  chatSidebarWidthAtom,
  layoutModeAtom
} from '../../atoms/v3/uiAtoms';
import { newTaskProjectIdAtom } from '../../atoms/v3/taskAtoms';
import { Task } from '../../types/task';
import { Project } from '../../types/project';
import { TaskList } from './components/tasks/TaskList';
import { ProjectInfoCard } from './components/projects/ProjectInfoCard';
import { ProjectEditModal } from './components/projects/ProjectEditModal';
import { ProjectShareModal } from './components/projects/ProjectShareModal';
import { ProjectDeleteModal } from './components/projects/ProjectDeleteModal';
import { DraggableTaskButton } from './components/tasks/DraggableTaskButton';
import { TaskDndContext, CreateTaskParams } from './components/tasks/TaskDndContext';
import { TaskDetailSheet } from './components/tasks/TaskDetailSheet';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';

/**
 * v3 版本的项目详情页
 */
export const ProjectDetail: React.FC = () => {
  const { t } = useTranslation(['v3/projects', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const location = useLocation();
  const fromPage = location.state?.from || 'tasks';

  // Jotai 状态
  const [projects] = useAtom(projectsAtom);
  const [tasks] = useAtom(tasksAtom);
  const [getProjectById] = useAtom(projectByIdAtom);
  const [, fetchTasks] = useAtom(fetchTasksAtom);
  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [, updateProject] = useAtom(updateProjectAtom);
  const [, deleteProject] = useAtom(deleteProjectAtom);

  // 本地状态
  const [project, setProject] = useState<Project | null>(null);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [optimisticUpdates, setOptimisticUpdates] = useState<Record<string, boolean>>({});

  // 编辑模态框状态
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<{
    id: string;
    title: string;
    description: string;
    folderId?: string | null;
  } | null>(null);

  // 分享模态框状态
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // 删除模态框状态
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // 任务详情浮层状态
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [, setNewTaskProjectIdAtom] = useAtom(newTaskProjectIdAtom);

  // 从 Jotai 状态加载项目
  const loadProjectFromJotai = useCallback((projectId: string) => {
    try {
      // 使用 getProjectById 函数获取项目
      const projectData = getProjectById(projectId);
      if (projectData) {
        setProject(projectData);
        setLoading(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading project from Jotai state:', error);
      return false;
    }
  }, [getProjectById]);

  // 从 Jotai 状态加载项目任务
  const loadTasksFromJotai = useCallback((projectId: string) => {
    try {
      // 直接从 tasksAtom 中过滤出属于该项目的任务
      const allTasks = tasks;
      if (allTasks && allTasks.length >= 0) {
        const filteredTasks = allTasks.filter(task => task.projectId === projectId);
        setProjectTasks(filteredTasks);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading tasks from Jotai state:', error);
      return false;
    }
  }, [tasks]);

  // 加载项目和任务数据
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      setLoading(true);

      // 尝试从 Jotai 状态加载项目
      const projectLoaded = loadProjectFromJotai(id);
      const tasksLoaded = loadTasksFromJotai(id);

      // 如果从 Jotai 状态加载失败，则从 API 加载
      if (!projectLoaded || !tasksLoaded) {
        try {
          // 确保项目和任务数据已加载
          await fetchProjects();
          await fetchTasks();

          // 再次尝试从 Jotai 状态加载
          const projectLoadedAfterFetch = loadProjectFromJotai(id);
          const tasksLoadedAfterFetch = loadTasksFromJotai(id);

          if (!projectLoadedAfterFetch) {
            setError(t('v3/projects:errors.projectNotFound'));
          }
        } catch (err) {
          setError(t('v3/projects:errors.loadingError'));
          console.error('Error loading project data:', err);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadData();
  }, [id, loadProjectFromJotai, loadTasksFromJotai, fetchProjects, fetchTasks]);

  // 格式化日期
  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: zhCN });
    } catch (error) {
      return t('common:dates.unknown');
    }
  };

  // 处理任务切换 - 使用 optimistic UI 更新
  const handleToggleTask = (taskId: string, completed: boolean) => {
    const task = projectTasks.find(t => t.id === taskId);
    if (task) {
      // 立即更新本地状态，实现 optimistic UI
      setOptimisticUpdates(prev => ({
        ...prev,
        [taskId]: completed
      }));

      // 更新本地任务列表
      setProjectTasks(prev =>
        prev.map(t =>
          t.id === taskId ? { ...t, done: completed } : t
        )
      );

      // 发送更新请求
      updateTask({
        id: taskId,
        done: completed,
      }).catch(() => {
        // 如果更新失败，回滚本地状态
        setOptimisticUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[taskId];
          return newUpdates;
        });

        // 回滚本地任务列表
        setProjectTasks(prev =>
          prev.map(t =>
            t.id === taskId ? { ...t, done: !completed } : t
          )
        );
      });
    }
  };

  // 处理任务点击 - 在当前页面打开任务详情，而不是导航到任务页面
  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
    setIsTaskDetailOpen(true);
  };

  // 处理创建任务 - 使用 useCallback 优化
  const handleCreateTask = useCallback((params: CreateTaskParams = {}) => {
    // 设置任务ID为null（表示新建任务）
    setSelectedTaskId(null);

    // 始终使用当前项目ID
    if (id) {
      setNewTaskProjectIdAtom(id);
    }

    // 打开任务详情浮层
    setIsTaskDetailOpen(true);
  }, [id, setNewTaskProjectIdAtom]);

  // 处理关闭任务详情浮层 - 使用 useCallback 优化
  const handleCloseTaskDetail = useCallback(() => {
    setIsTaskDetailOpen(false);
    // 延迟清除任务ID，确保关闭动画完成后再清除
    setTimeout(() => {
      setSelectedTaskId(null);
    }, 300);
  }, []);

  // 处理编辑项目
  const handleEditProject = () => {
    if (project) {
      setEditingProject({
        id: project.id,
        title: project.title,
        description: project.context || '',
        folderId: project.folderId
      });
      setIsEditModalOpen(true);
    }
  };

  // 处理分享项目
  const handleShareProject = () => {
    setIsShareModalOpen(true);
  };

  // 处理删除项目
  const handleDeleteProject = () => {
    setIsDeleteModalOpen(true);
  };

  // 处理确认删除项目
  const handleConfirmDelete = async (cascadeDelete: boolean) => {
    if (!project || !id) return;

    try {
      // 调用API删除项目
      await deleteProject(id, cascadeDelete);

      // 显示成功提示
      console.log(`Project ${id} deleted successfully with cascadeDelete=${cascadeDelete}`);

      // 导航回任务列表
      navigate('/v3/tasks');
    } catch (error) {
      console.error('删除项目失败:', error);
      setError(t('v3/projects:errors.deleteFailed'));
    }
  };

  // 处理保存项目
  const handleSaveProject = async (title: string, description: string, folderId?: string | null): Promise<boolean> => {
    if (!editingProject) return false;

    try {
      console.log('ProjectDetail: handleSaveProject 收到参数:', { title, description, folderId });
      const success = await updateProject({
        id: editingProject.id,
        title,
        context: description,
        folderId
      });

      return !!success;
    } catch (error) {
      console.error(t('v3/projects:errors.saveProjectFailed'), error);
      return false;
    }
  };

  // 关闭编辑模态框
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingProject(null);
  };

  // 关闭分享模态框
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  // 返回上一页
  const handleBack = () => {
    navigate(-1);
  };

  // 计算统计数据
  const tasksTotal = projectTasks.length;
  const tasksVital = projectTasks.filter(task => task.vital).length;
  const tasksUrgent = projectTasks.filter(task => task.urgent).length;
  const tasksCompleted = projectTasks.filter(task => task.done).length;

  // 获取布局状态
  const [layoutMode] = useAtom(layoutModeAtom);
  const [sidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const [chatSidebarCollapsed] = useAtom(chatSidebarCollapsedAtom);
  const [chatSidebarWidth] = useAtom(chatSidebarWidthAtom);

  // 渲染内容
  return (
    <TaskDndContext onCreateTask={handleCreateTask}>
      <div className="h-full flex flex-col bg-ios-gray6 dark:bg-ios-dark-gray6">
        {/* 顶部导航栏 - 使用与 ChatDetail 相同的样式，根据布局模式应用不同的样式 */}
        <div
          className={`fixed pt-[env(safe-area-inset-top)] top-0 z-40 flex items-center justify-between px-4 py-2 bg-white/80 dark:bg-ios-dark-gray6/80 backdrop-blur-ios border-b border-ios-gray5 dark:border-ios-dark-gray5 transition-all duration-300 ease-in-out ${
            layoutMode === 'desktop'
              ? `${sidebarCollapsed ? 'left-[4.375rem]' : 'left-[16.25rem]'}`
              : 'left-0 right-0'
          }`}
          style={layoutMode === 'desktop' ? {
            right: chatSidebarCollapsed ? '0' : `${chatSidebarWidth}rem`
          } : undefined}
        >
        <div className="flex-shrink-0 min-w-[3.75rem] w-[20%] max-w-[5rem]">
          <button
            onClick={handleBack}
            className="flex items-center text-ios-blue dark:text-ios-dark-blue py-2 -ml-2"
          >
            <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-ios-body font-medium truncate">{t('common:tasks')}</span>
          </button>
        </div>

        <div className="flex-1 text-center px-1 mx-1 min-w-0">
          <h2 className="text-lg font-semibold text-ios-black dark:text-white truncate">
            {t('v3/projects:projectDetails')}
          </h2>
        </div>

        <div className="flex-shrink-0 min-w-[3.75rem] w-[20%] max-w-[5rem]">
          {/* 右侧空白区域，保持布局平衡 */}
        </div>
      </div>

      {/* 内容区域 - 添加顶部内边距以适应固定导航栏 */}
      <div className="flex-1 overflow-y-auto pb-[calc(4rem+env(safe-area-inset-bottom))] px-4 pt-20">
        <AnimatePresence mode="wait">
          {loading ? (
            // 加载状态
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-12"
            >
              <div className="w-8 h-8 border-2 border-ios-blue dark:border-ios-dark-blue border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-ios-body text-ios-gray dark:text-ios-dark-gray">{t('common:loadingText')}...</p>
            </motion.div>
          ) : error || !project ? (
            // 错误状态
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-12"
            >
              <div className="w-16 h-16 text-ios-red dark:text-ios-dark-red">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="mt-4 text-ios-body text-ios-gray dark:text-ios-dark-gray">
                {error || t('v3/projects:errors.projectNotFound')}
              </p>
              <button
                onClick={handleBack}
                className="mt-6 px-4 py-2 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-full text-ios-subhead font-medium"
              >
                {t('v3/projects:actions.backToTaskList')}
              </button>
            </motion.div>
          ) : (
            // 项目详情
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {/* 项目信息卡片 */}
              <ProjectInfoCard
                project={project}
                tasksTotal={tasksTotal}
                tasksVital={tasksVital}
                tasksUrgent={tasksUrgent}
                tasksCompleted={tasksCompleted}
                formatDate={formatDate}
                onEditClick={handleEditProject}
                onShare={handleShareProject}
                onDelete={handleDeleteProject}
              />

              {/* 项目任务列表 */}
              <div>
                <h2 className="text-ios-title2 font-semibold text-ios-black dark:text-white mb-4 px-0">
                  {t('v3/projects:taskList')}
                </h2>
                {projectTasks.length > 0 ? (
                  <>
                    {/* 未完成任务 */}
                    {projectTasks.some(task => !task.done) && (
                      <TaskList
                        tasks={projectTasks.filter(task => !task.done)}
                        title={t('v3/projects:taskStatus.incomplete')}
                        hideProjectName={true}
                        onToggleTask={handleToggleTask}
                        onTaskClick={handleTaskClick}
                        noMargin={true}
                        projectId={project.id}
                        isTimeGroup={false}
                      />
                    )}

                    {/* 添加一个小的间距 */}
                    {projectTasks.some(task => !task.done) && projectTasks.some(task => task.done) && (
                      <div className="h-3"></div>
                    )}

                    {/* 已完成任务 */}
                    {projectTasks.some(task => task.done) && (
                      <TaskList
                        tasks={projectTasks.filter(task => task.done)}
                        title={t('v3/projects:taskStatus.completed')}
                        hideProjectName={true}
                        onToggleTask={handleToggleTask}
                        onTaskClick={handleTaskClick}
                        noMargin={true}
                        projectId={project.id}
                        isTimeGroup={false}
                      />
                    )}
                  </>
                ) : (
                  <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark p-6 text-center">
                    <p className="text-ios-body text-ios-gray dark:text-ios-dark-gray">
                      {t('v3/projects:emptyState.noTasks')}
                    </p>
                  </div>
                )}
              </div>

              {/* 添加额外的空白区域，确保页面有足够的内容可以滚动 */}
              <div className="h-[10rem]"></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 项目编辑模态框 */}
      {editingProject && project && (
        <ProjectEditModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSaveProject}
          projectId={editingProject.id}
          initialTitle={editingProject.title}
          initialDescription={editingProject.description}
          initialFolderId={editingProject.folderId}
        />
      )}

      {/* 项目分享模态框 */}
      {project && (
        <ProjectShareModal
          isOpen={isShareModalOpen}
          onClose={handleCloseShareModal}
          project={project}
        />
      )}

      {/* 项目删除模态框 */}
      {project && (
        <ProjectDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          projectTitle={project.title}
          tasksCount={projectTasks.length}
        />
      )}

      {/* 添加任务按钮 - 可拖拽 */}
      <DraggableTaskButton onClick={() => handleCreateTask({})} />

      {/* 任务详情浮层 */}
      <TaskDetailSheet
        isOpen={isTaskDetailOpen}
        onClose={handleCloseTaskDetail}
        taskId={selectedTaskId}
        projectId={id}
      />
    </div>
  </TaskDndContext>
  );
};
